// useAuth.tsx — sign-in + entitlement state for the whole app.
//
// The app is an identity client of the service-wide Supabase user. When Supabase
// isn't configured, access is always 'member' (ungated). When it is: 'signedout'
// shows the sign-in step (app still browsable), 'free' caps the programme at the
// sign-up step, 'member' is full access. Sales happen on the website; the Stripe
// webhook flips entitlement server-side (read via recheck / on relaunch).
import React from 'react';
import { App as CapApp } from '@capacitor/app';
import { ACTIVATION_ENABLED } from '@/config/supabase';
import {
  getSession,
  cachedSession,
  onAuthStateChange,
  requestOtp,
  signInWithReviewPin,
  verifyOtp,
  signOut as doSignOut,
  startSignup,
  recheckEntitlement,
  accessFromSession,
  trialActive as svcTrialActive,
  trialDaysLeft as svcTrialDaysLeft,
  lastCheckedAt,
  type AuthSession,
  type Access,
} from '@/services/auth';
import { redeemOrgCode as svcRedeemOrgCode } from '@/services/sync';
import { iapLogIn, iapLogOut } from '@/services/purchases';

export type OrgUnavailableReason = 'seats_full' | 'expired' | 'cancelled';
export type RedeemResult = { granted: boolean; error?: string; noDomainMatch?: boolean; termMonths?: number; orgName?: string; reason?: OrgUnavailableReason };

// A one-shot post-sign-in welcome, set after access resolves. 'member' = already a
// paying/returning member; 'org' = just activated via a coupon or domain licence
// (provided by `orgName`); 'org_unavailable' = a RECOGNISED org (code or domain) but
// its licence is spent/expired/cancelled, so the user is on free for now; 'trial' =
// free. Shown once, then dismissed.
export type Welcome = {
  kind: 'member' | 'org' | 'trial' | 'org_unavailable';
  orgName?: string;
  termMonths?: number;
  reason?: OrgUnavailableReason;
};

// Foreground entitlement re-checks are capped to once per day: a seat rarely
// changes, and each check is a network read we don't want to fire on every
// app switch (especially on a flaky connection).
const RECHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

interface AuthApi {
  ready: boolean;
  enabled: boolean;
  session: AuthSession | null;
  access: Access;
  isMember: boolean;
  /** Where the membership came from ('stripe' | 'apple' | 'google' | 'org' | 'manual'), or null. */
  entitlementSource: string | null;
  /** A signed-in non-member still inside the 10-day free trial (full access). */
  trialActive: boolean;
  /** Whole days left in the free trial (0 once expired), or null if unknown / member. */
  trialDaysLeft: number | null;
  /** True once the trial has lapsed for a non-member: the app's premium FEATURES
   *  lock (with a buy-to-unlock prompt). Content is separately capped at chapter 4
   *  for ALL non-members. Always-free items (self-compassion, grounding, roll-the-
   *  dice, and the Calm "panic" route) ignore this. */
  featuresLocked: boolean;
  signingIn: boolean;
  /** True while a foreground entitlement re-check is in flight (show a spinner). */
  checking: boolean;
  /** Set when the last sign-in attempt failed; cleared on a new attempt. */
  error: string | null;
  clearError: () => void;
  /** Send the email OTP. `firstName` (collected at creation) is carried as signup
   *  metadata so a brand-new account's profiles.first_name is seeded server-side. */
  sendOtp: (email: string, firstName?: string) => Promise<boolean>;
  /** Verify the email code (sign in / create account). `firstName` is written to the
   *  profile on creation. Optionally pass an organisation `orgCode`; after sign-in we
   *  resolve access: already a member → keep it; else a code → redeem it; else try the
   *  verified email domain; else free. */
  confirmOtp: (email: string, code: string, orgCode?: string, firstName?: string) => Promise<boolean>;
  /** App review: sign in the fixed review address with its PIN (Supabase password). */
  confirmReviewPin: (email: string, pin: string) => Promise<boolean>;
  signUp: () => Promise<void>;
  signOut: () => void;
  /** Re-read entitlement now. Resolves TRUE when the account has membership, so a
   *  caller can answer the tap: the unlock walls say so and close themselves, rather
   *  than leaving the user looking at an unchanged screen. */
  recheck: () => Promise<boolean>;
  /** Redeem an organisation code (or '' to qualify by verified email domain),
   *  then refresh entitlement. Returns the outcome for the UI. */
  redeemOrgCode: (code: string) => Promise<RedeemResult>;
  /** One-shot welcome to show after a fresh sign-in (null once dismissed). */
  welcome: Welcome | null;
  dismissWelcome: () => void;
}

const AuthContext = React.createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const enabled = ACTIVATION_ENABLED();
  // Seed from the on-device cache so a returning user renders INSTANTLY (no
  // network wait). ready starts true — we never block first paint on the
  // membership check; it runs in the background and updates state when it lands.
  const [session, setSession] = React.useState<AuthSession | null>(() => (enabled ? cachedSession() : null));
  const [ready] = React.useState(true);
  const [signingIn, setSigningIn] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [welcome, setWelcome] = React.useState<Welcome | null>(null);

  const clearError = React.useCallback(() => setAuthError(null), []);
  const dismissWelcome = React.useCallback(() => setWelcome(null), []);

  // Keep RevenueCat's appUserID == the Supabase user id (cold start with a cached
  // session, sign-in, and sign-out all flow through here). No-ops without SHOW_IAP.
  const uid = session?.user.userId ?? null;
  React.useEffect(() => {
    if (!enabled) return;
    if (uid && uid !== 'local') iapLogIn(uid).catch(() => { /* purchase just stays unavailable */ });
    else if (!uid) iapLogOut().catch(() => undefined);
  }, [enabled, uid]);

  // Restore session on launch + subscribe to changes; catch the native OAuth deep link.
  React.useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // First paint already used the cached session (above), so we DON'T block on the
    // network. Fetch the live session + entitlement in the BACKGROUND and swap it in
    // when it lands. buildSession falls back to the cached session on a slow/offline
    // read, so a paying member is never bounced to the login wall by a dropped
    // connection — and a genuinely signed-out user is corrected to the wall here.
    getSession()
      .then((s) => { if (!cancelled) setSession(s); })
      .catch(() => { /* keep the cached session */ });
    const unsub = onAuthStateChange((s) => { if (!cancelled) setSession(s); });
    // Re-read entitlement every time the app returns to the foreground. A seat can
    // change server-side with NO auth event (a cancellation/refund flips
    // profiles.entitlement to 'free'; a web purchase flips it to 'member'), so we
    // can't rely on onAuthStateChange — we re-fetch the profile on every resume.
    const resumeP = CapApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
      if (!isActive) return;
      if (Date.now() - lastCheckedAt() < RECHECK_INTERVAL_MS) return; // once/day max
      setChecking(true);
      getSession()
        .then((s) => { if (!cancelled) setSession(s); })
        .finally(() => { if (!cancelled) setChecking(false); });
    });
    // A web purchase deep-links back into the app (org.renurture.app://purchase-complete)
    // so we reflect the new membership AT ONCE — bypassing the once/day cap above, because
    // this is an explicit "I just paid" signal, not a routine resume. getSession() fails
    // soft to the cached session, so a flaky read never bounces a member to the wall.
    const urlP = CapApp.addListener('appUrlOpen', ({ url }: { url: string }) => {
      if (!url || !/purchase|member|refresh/i.test(url)) return;
      setChecking(true);
      getSession()
        .then((s) => { if (!cancelled) setSession(s); })
        .finally(() => { if (!cancelled) setChecking(false); });
    });
    return () => {
      cancelled = true;
      unsub();
      resumeP.then((h) => h.remove());
      urlP.then((h) => h.remove());
    };
  }, [enabled]);

  const sendOtp = React.useCallback(async (email: string, firstName?: string) => {
    setAuthError(null);
    const res = await requestOtp(email, firstName);
    if (!res.ok) setAuthError(res.error ?? 'We couldn’t send the code. Check the email and try again.');
    return res.ok;
  }, []);

  // APP REVIEW sign-in: PIN instead of an emailed code, for the one fixed review
  // address. Kept separate from confirmOtp so none of the org/domain redemption runs
  // for it — a reviewer should see the ordinary app, not somebody's licence.
  const confirmReviewPin = React.useCallback(async (email: string, pin: string) => {
    setAuthError(null); setSigningIn(true);
    const s = await signInWithReviewPin(email, pin);
    if (!s) { setSigningIn(false); setAuthError('That PIN didn’t work. Please try again.'); return false; }
    setSession(s);
    // Derive the welcome from the session, exactly as confirmOtp does. Hardcoding 'trial'
    // told a member account it was on a trial, which is both wrong and the first thing a
    // reviewer would see.
    setWelcome(accessFromSession(s) === 'member' ? { kind: 'member' } : { kind: 'trial' });
    setSigningIn(false);
    return true;
  }, []);

  const confirmOtp = React.useCallback(async (email: string, code: string, orgCode?: string, firstName?: string) => {
    setAuthError(null); setSigningIn(true);
    let s = await verifyOtp(email, code, firstName);
    if (!s) { setSigningIn(false); setAuthError('That code didn’t work. Please try again.'); return false; }
    setSession(s);
    // Resolve access in priority order — the unified org/coupon/domain flow:
    //   already a member (paid or redeemed before) → keep it;
    //   else an org code → redeem it; else try the VERIFIED email domain;
    //   else stay free (trial). Fail-soft: a redeem hiccup never blocks sign-in.
    let welcomeState: Welcome = { kind: 'trial' };
    try {
      if (accessFromSession(s) === 'member') {
        welcomeState = { kind: 'member' };
      } else if (s.accessToken && s.accessToken !== 'local') {
        const res = await svcRedeemOrgCode(s.accessToken, (orgCode || '').trim());
        if (res.granted) {
          s = (await recheckEntitlement()) ?? s; setSession(s);
          welcomeState = { kind: 'org', orgName: res.orgName, termMonths: res.termMonths };
        } else if (res.reason) {
          // Recognised org (a real code, or a matched registered domain) but the
          // licence is spent / expired / cancelled. Tell them kindly on the welcome
          // screen instead of dropping silently to free — covers code AND domain.
          welcomeState = { kind: 'org_unavailable', orgName: res.orgName, reason: res.reason };
        } else if ((orgCode || '').trim() && !res.noDomainMatch) {
          setAuthError(res.error || 'That code could not be used.');
        }
      }
    } catch { /* fail-soft — they're signed in as free */ }
    setWelcome(welcomeState);
    setSigningIn(false);
    return true;
  }, []);

  const signUp = React.useCallback(async () => { await startSignup(); }, []);
  const signOut = React.useCallback(() => { doSignOut(); setSession(null); }, []);
  const recheck = React.useCallback(async (): Promise<boolean> => {
    setChecking(true);
    try {
      const s = await recheckEntitlement();
      if (s) setSession(s); // fail-soft: keep session if the read returns nothing
      // A read that didn't land (offline / timed out) is reported as "not confirmed",
      // not as a demotion: the session above is untouched, so nobody loses access —
      // the caller just can't tell them they're in yet.
      return !enabled || (!!s && accessFromSession(s) === 'member');
    } finally { setChecking(false); }
  }, [enabled]);

  const redeemOrgCode = React.useCallback(async (code: string): Promise<RedeemResult> => {
    // Read a fresh session so a redeem fired right after sign-in uses the new token.
    const s = await getSession();
    const token = s?.accessToken;
    if (!token || token === 'local') return { granted: false, error: 'Please sign in first.' };
    const res = await svcRedeemOrgCode(token, code);
    if (res.granted) setSession(await recheckEntitlement()); // reflect new membership at once
    return res;
  }, []);

  const access: Access = enabled ? accessFromSession(session) : 'member';
  const isMember = !enabled || access === 'member';
  // Trial only applies when activation is on (ungated dev is always full access).
  const trialActive = enabled ? svcTrialActive(session) : false;
  const trialDaysLeft = enabled ? svcTrialDaysLeft(session) : null;
  // Features lock once a signed-in non-member's trial has lapsed (enabled, not a
  // member, past the 10 days). Members and trial users keep features unlocked.
  const featuresLocked = enabled && !isMember && !!session && !trialActive;

  const api = React.useMemo<AuthApi>(
    () => ({
      ready, enabled, session, access, isMember, entitlementSource: session?.entitlementSource ?? null, trialActive, trialDaysLeft, featuresLocked, signingIn, checking,
      error: authError, clearError,
      sendOtp, confirmOtp, confirmReviewPin,
      signUp, signOut, recheck, redeemOrgCode,
      welcome, dismissWelcome,
    }),
    [ready, enabled, session, access, isMember, trialActive, trialDaysLeft, featuresLocked, signingIn, checking, authError, clearError,
     sendOtp, confirmOtp, confirmReviewPin, signUp, signOut, recheck, redeemOrgCode, welcome, dismissWelcome],
  );

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}

/** Like useAuth but null outside a provider (for hooks that should quietly do nothing there). */
export function useAuthOptional(): AuthApi | null {
  return React.useContext(AuthContext);
}

export function useAuth(): AuthApi {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
